import { useCallback, useEffect, useRef, useState } from 'react'
import { ArrowUpRight, Instagram, Menu, X } from 'lucide-react'
import { Link, NavLink, Outlet } from 'react-router-dom'
import { useFocusTrap } from '../../hooks/useFocusTrap.js'
import { useAuth } from '../../features/auth/AuthContext.jsx'
import { BrandMark } from '../ui/BrandMark.jsx'
import { ButtonLink } from '../ui/Button.jsx'
import { platformSettingsApi } from '../../services/plotfyApi.js'
import { getContactSettings } from '../../data/demoStore.js'

const navigation = [
  { label: 'Campaign', to: '/explore' },
  { label: 'Untuk Creator', to: '/#creator' },
  { label: 'Untuk Brand', to: '/#brand' },
  { label: 'Tier Kreator', to: '/#tier-kreator' },
  { label: 'Cara Kerja', to: '/#cara-kerja' },
]

export function PublicLayout() {
  const [menuOpen, setMenuOpen] = useState(false)
  const drawerRef = useRef(null)
  const closeMenu = useCallback(() => setMenuOpen(false), [])
  const { user, roleHome } = useAuth()
  useFocusTrap(drawerRef, menuOpen, closeMenu)

  const [contactInfo, setContactInfo] = useState({
    contactEmail: 'halo@plotfy.id',
    instagramHandle: 'Instagram',
    instagramUrl: 'https://instagram.com',
  })

  useEffect(() => {
    let active = true
    platformSettingsApi.getPublicSettings()
      .then((settings) => {
        if (active && settings) {
          setContactInfo({
            contactEmail: settings.contactEmail || 'halo@plotfy.id',
            instagramHandle: settings.instagramHandle || 'Instagram',
            instagramUrl: settings.instagramUrl || 'https://instagram.com',
          })
        }
      })
      .catch(() => {
        if (active) {
          setContactInfo(getContactSettings())
        }
      })

    const handleSettingsChanged = () => {
      setContactInfo(getContactSettings())
    }
    const handleDataChanged = (e) => {
      if (!e?.detail || e.detail.entity === 'settings') {
        platformSettingsApi.getPublicSettings().then((settings) => {
          if (settings) {
            setContactInfo({
              contactEmail: settings.contactEmail || 'halo@plotfy.id',
              instagramHandle: settings.instagramHandle || 'Instagram',
              instagramUrl: settings.instagramUrl || 'https://instagram.com',
            })
          }
        }).catch(() => {})
      }
    }
    window.addEventListener('plotfy:demo-settings-changed', handleSettingsChanged)
    window.addEventListener('plotfy:data-changed', handleDataChanged)
    return () => {
      active = false
      window.removeEventListener('plotfy:demo-settings-changed', handleSettingsChanged)
      window.removeEventListener('plotfy:data-changed', handleDataChanged)
    }
  }, [])

  return (
    <div className="min-h-screen bg-ink-950">
      <a href="#main-content" className="fixed left-4 top-3 z-[100] -translate-y-20 rounded-lg bg-brand-600 px-4 py-2 text-sm font-semibold text-white transition-transform focus:translate-y-0">
        Lewati ke konten
      </a>
      <header className="sticky top-0 z-50 border-b border-line bg-ink-950/95 backdrop-blur-md">
        <nav className="container-app flex h-[68px] items-center justify-between gap-5" aria-label="Navigasi utama">
          <BrandMark />
          <div className="hidden items-center gap-1 lg:flex">
            {navigation.map((item) => (
              <NavLink key={item.label} to={item.to} className="rounded-lg px-3 py-2 text-sm font-medium text-copy-muted transition-colors hover:bg-ink-850 hover:text-copy">
                {item.label}
              </NavLink>
            ))}
          </div>
          <div className="hidden items-center gap-2 sm:flex">
            {user ? (
              <ButtonLink to={roleHome} variant="secondary">Buka Dashboard</ButtonLink>
            ) : (
              <>
                <ButtonLink to="/login" variant="ghost">Masuk</ButtonLink>
                <ButtonLink to="/login?intent=register">Daftar</ButtonLink>
              </>
            )}
          </div>
          <button type="button" onClick={() => setMenuOpen(true)} className="grid size-11 place-items-center rounded-[11px] border border-line bg-ink-900 text-copy sm:hidden" aria-label="Buka menu" aria-expanded={menuOpen}>
            <Menu className="size-5" aria-hidden="true" />
          </button>
        </nav>
      </header>

      {menuOpen && (
        <div className="fixed inset-0 z-[70] bg-black/75 sm:hidden" onMouseDown={(event) => event.target === event.currentTarget && closeMenu()}>
          <aside ref={drawerRef} role="dialog" aria-modal="true" aria-label="Menu navigasi" className="ml-auto flex h-full w-[min(88vw,360px)] flex-col overflow-y-auto border-l border-line-strong bg-ink-900 p-5 shadow-2xl">
            <div className="flex items-center justify-between">
              <BrandMark />
              <button type="button" onClick={closeMenu} className="grid size-11 place-items-center rounded-[11px] border border-line text-copy" aria-label="Tutup menu">
                <X className="size-5" aria-hidden="true" />
              </button>
            </div>
            <div className="mt-8 flex flex-col gap-1">
              {navigation.map((item) => (
                <Link key={item.label} to={item.to} onClick={closeMenu} className="flex min-h-12 items-center justify-between rounded-xl px-3.5 text-base font-medium text-copy-secondary hover:bg-ink-800 hover:text-copy">
                  {item.label}<ArrowUpRight className="size-4 text-copy-disabled" aria-hidden="true" />
                </Link>
              ))}
            </div>
            <div className="mt-auto grid gap-2 pt-8">
              {user ? (
                <ButtonLink to={roleHome} onClick={closeMenu}>Buka Dashboard</ButtonLink>
              ) : (
                <>
                  <ButtonLink to="/login" variant="secondary" onClick={closeMenu}>Masuk</ButtonLink>
                  <ButtonLink to="/login?intent=register" onClick={closeMenu}>Daftar Gratis</ButtonLink>
                </>
              )}
            </div>
          </aside>
        </div>
      )}

      <main id="main-content"><Outlet /></main>
      <footer className="border-t border-line bg-ink-900 py-10 sm:py-14">
        <div className="container-app grid gap-9 sm:grid-cols-2 lg:grid-cols-[1.4fr_1fr_1fr]">
          <div>
            <BrandMark />
            <p className="mt-4 max-w-sm text-sm leading-6 text-copy-muted">Tempat brand lokal dan creator TikTok bertumbuh lewat campaign yang jelas, terukur, dan manusiawi.</p>
          </div>
          <div>
            <p className="text-sm font-semibold text-copy">Jelajahi</p>
            <div className="mt-3 grid gap-2 text-sm text-copy-muted">
              <Link to="/explore" className="w-fit hover:text-brand-300">Campaign</Link>
              <Link to="/#creator" className="w-fit hover:text-brand-300">Untuk Creator</Link>
              <Link to="/#brand" className="w-fit hover:text-brand-300">Untuk Brand</Link>
            </div>
          </div>
          <div>
            <p className="text-sm font-semibold text-copy">Plotfy</p>
            <div className="mt-3 grid gap-2 text-sm text-copy-muted">
              <a href={`mailto:${contactInfo.contactEmail}`} className="w-fit hover:text-brand-300">
                {contactInfo.contactEmail}
              </a>
              <a
                href={contactInfo.instagramUrl.startsWith('http') ? contactInfo.instagramUrl : `https://${contactInfo.instagramUrl}`}
                target="_blank"
                rel="noreferrer"
                className="flex w-fit items-center gap-2 hover:text-brand-300"
              >
                <Instagram className="size-4" aria-hidden="true" /> {contactInfo.instagramHandle}
              </a>
              <Link to="/terms" className="w-fit hover:text-brand-300">
                Syarat & Ketentuan
              </Link>
            </div>
          </div>
        </div>
        <div className="container-app mt-9 border-t border-line pt-5 text-xs text-copy-disabled">CopyRight © {new Date().getFullYear()} PT Media Cakrawala Mci.</div>
      </footer>
    </div>
  )
}
